import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import NumericInput from 'react-numeric-input';
import ModifierComponent from './ModifierComponent';
import './App.css';
import { ID, precisionRound } from './Utils';
import { Line } from 'react-chartjs-2';
import 'chartjs-plugin-datalabels';
import MyTooltip from './MyTooltip';
import ReactFileReader from 'react-file-reader';
import FileSaver from './FileSaver';

export default class Projector extends Component {
  constructor(props) {
    super(props);
    this.timeout = null;
    if(props.state)
      this.state = props.state;
    else
      this.state = {
        revenueChartData: null,
        trafficChartData: null,
        mrr: 5000,
        arpu: 20,
        churn: 5,
        traffic: 5000,
        bank: 0,
        trial_conversion: 0,
        subscriber_conversion: 0.3,
        expenses: 10000,
        period: 24,
        status: "",
        shownChart: 1,
        modifiers: [],
        showModifiers: true,
        showInitial: true
      };

    this.chartOptions = {
      componentState: this.state,
      responsive: true,
      plugins: {
        datalabels: {
          backgroundColor: function(context) {
            return context.dataset.backgroundColor;
          },
          align: function(context) {
            if(context.dataIndex < context.chart.options.componentState.period / 2)
              return 315;
            else
              return 225;
          },
          borderColor: "#444444",
          borderWidth: 1,
          borderRadius: 10,
          color: "#444444",
          formatter: function(value, context) {
              // See if there's a modifier on this index and type
              if(context.dataset._meta[0].hidden === true)
                return null;
              var modifiers = context.chart.options.componentState.modifiers.filter(modifier =>
                (modifier.isValid && modifier.isActive &&
                  modifier.month === context.dataIndex+1 &&
                  context.dataset.label.toLowerCase().indexOf(modifier.type) > -1)
                );
              if(modifiers.length > 0 && modifiers[0].annotation !== "")
                return modifiers[0].annotation;
              return null;
          }
        }
      }
    };
  }

  calculateModifierPoints(modifier, points, startMonth, recurring=true) {
    var month;

    if(modifier.interpolation === "linear") {
      var increment = (modifier.value - points[startMonth-1]) / (modifier.month - startMonth);
      for(month=startMonth;month<modifier.month;month++) {
				if(recurring)
        	points.push(points[month-1] + increment);
				else
					points.push(increment);
      }
    }

    if(modifier.interpolation === "none") {
      for(month=startMonth;month<modifier.month-1;month++) {
				if(recurring)
        	points.push(points[month-1]);
				else
					points.push(0);
      }
      points.push(modifier.value);
    }
  	return points;
  }

  componentWillMount() {
    this.initData();
  }

  initData() {
    var currentState = this.state;
    // Delete generated data as we don't need to store these.
    delete currentState.revenueChartData;
    delete currentState.trafficChartData;
    // Persist in local storage
    localStorage.setItem("projectorState", JSON.stringify(currentState));
    var mrr_points = [this.state.mrr];
    var subscriber_conversion_points = [this.state.subscriber_conversion];
    var trial_conversion_points = [this.state.trial_conversion];
    var bank_points = [this.state.bank];
		var bank_change_points = [0];
    var mrr_gain_points = [0];
    var expense_points = [this.state.expenses];
    var churn_points = [this.state.churn];
    var arpu_points  = [this.state.arpu];
    var revenue_churn_points = [Math.round([this.state.mrr * this.state.churn / 100], 2)];
    var traffic_points = [this.state.traffic];
    var profit_points = [this.state.mrr - this.state.expenses];
    var labels = [];
    // Iterate over each period to apply any modifiers and do transitions between the values.
    var churnEnd = 1;
    var arpuEnd = 1;
    var trafficEnd = 1;
    var expenseEnd = 1;
    var trialConversionEnd = 1;
    var subscriberConversionEnd = 1;
		var bankChangeEnd = 1;
    var month;

    var modifiers = this.state.modifiers.sort((a,b) => a.month >= b.month);
    modifiers.forEach((modifier, index) => {
      if(modifier.isValid && modifier.isActive) {
        if(modifier.type === "churn") {
          churn_points = this.calculateModifierPoints(modifier, churn_points, churnEnd);
          churnEnd = modifier.month;
        }
        if(modifier.type === "arpu") {
          arpu_points = this.calculateModifierPoints(modifier, arpu_points, arpuEnd);
          arpuEnd = modifier.month;
        }
        if(modifier.type === "traffic") {
          traffic_points = this.calculateModifierPoints(modifier, traffic_points, trafficEnd);
          trafficEnd = modifier.month;
        }
        if(modifier.type === "expenses") {
           expense_points = this.calculateModifierPoints(modifier, expense_points, expenseEnd);
           expenseEnd = modifier.month;
        }
        if(modifier.type === "subscriber_conversion") {
           subscriber_conversion_points = this.calculateModifierPoints(modifier, subscriber_conversion_points, subscriberConversionEnd);
           subscriberConversionEnd = modifier.month;
        }
        if(modifier.type === "trial_conversion") {
           trial_conversion_points = this.calculateModifierPoints(modifier, trial_conversion_points, trialConversionEnd);
           trialConversionEnd = modifier.month;
        }
				if(modifier.type === "bank_change") {
           bank_change_points = this.calculateModifierPoints(modifier, bank_change_points, bankChangeEnd, false);
           bankChangeEnd = modifier.month;
        }
      }
    });

    var ending_churn_rate = churn_points[churn_points.length-1];
    for(month=churnEnd;month<this.state.period+1;month++) {
      churn_points.push(ending_churn_rate);
    }

    var ending_arpu = arpu_points[arpu_points.length-1];
    for(month=arpuEnd;month<this.state.period+1;month++) {
      arpu_points.push(ending_arpu);
    }

    var ending_traffic = traffic_points[traffic_points.length-1];
    for(month=trafficEnd;month<this.state.period+1;month++) {
      traffic_points.push(ending_traffic);
    }

    var ending_expense = expense_points[expense_points.length-1];
    for(month=expenseEnd;month<this.state.period+1;month++) {
      expense_points.push(ending_expense);
    }

    var ending_conversion = subscriber_conversion_points[subscriber_conversion_points.length-1];
    for(month=subscriberConversionEnd;month<this.state.period+1;month++) {
      subscriber_conversion_points.push(ending_conversion);
    }

    ending_conversion = trial_conversion_points[trial_conversion_points.length-1];
    for(month=trialConversionEnd;month<this.state.period+1;month++) {
      trial_conversion_points.push(ending_conversion);
    }

		for(month=bankChangeEnd;month<this.state.period+1;month++) {
			bank_change_points.push(0);
		}

    var total_mrr = this.state.mrr;
    // Iterate over each period to calculate traffic, revenue and churn
    for(month=1;month<this.state.period+1;month++) {
      labels.push(month);
      // Subtract churn
      var new_mrr = mrr_points[month-1] - revenue_churn_points[month-1];
      // New traffic
      // Added mrr from traffic
      if(trial_conversion_points[month-1] === 0)
        new_mrr += Math.round(traffic_points[month-1] * (subscriber_conversion_points[month-1] / 100) * arpu_points[month-1], 2);
      else
        new_mrr += Math.round(traffic_points[month-1] * (trial_conversion_points[month-1] / 100) * (subscriber_conversion_points[month-1] / 100) * arpu_points[month-1], 2);

      mrr_gain_points.push(new_mrr - mrr_points[month-1]);
      mrr_points.push(new_mrr);
      revenue_churn_points.push(Math.round(new_mrr * churn_points[month-1] / 100, 2));
      profit_points.push(new_mrr - expense_points[month-1]);
      bank_points.push(bank_points[month-1] + profit_points[month] + bank_change_points[month]);
      total_mrr += new_mrr - expense_points[month-1];
    }

    var ltv = precisionRound(arpu_points[arpu_points.length-1]/(churn_points[churn_points.length-1]/100), 2);

    this.setState({
      status: "Made $" + total_mrr + ", LTV is: $" + precisionRound(ltv, 2) + " which means we can spend $" + precisionRound(ltv/3, 2) + " for customer acquisition.",
      revenueChartData: {
      	labels,
      	datasets: [
      		{
      			label: "MRR",
      			backgroundColor: "rgba(0,220,220,0.2)",
      			data: mrr_points
      		},
          {
      			label: "MRR Gain",
      			backgroundColor: "rgba(0,220,20,0.2)",
      			data: mrr_gain_points
      		},
          {
      			label: "Expenses",
      			backgroundColor: "rgba(220,20,220,0.2)",
      			data: expense_points
      		},
          {
      			label: "Revenue Churn",
      			backgroundColor: "rgba(255,20,10,0.2)",
      			data: revenue_churn_points
      		},
          {
      			label: "ARPU",
      			backgroundColor: "rgba(0,20,10,0.2)",
      			data: arpu_points
      		},
          {
            label: "Traffic",
            backgroundColor: "rgba(0,0,200,0.5)",
      			data: traffic_points
          },
          {
            label: "Profit",
            backgroundColor: "rgba(0,255,0,0.5)",
      			data: profit_points
          },
          {
            label: "Bank",
            backgroundColor: "rgba(255,0,255,0.7)",
      			data: bank_points
          }
      	]
      },
      trafficChartData: {
      	labels,
      	datasets: [
      		{
      			label: "Traffic",
      			backgroundColor: "rgba(0,220,220,0.2)",
      			data: traffic_points
      		}
      	]
      }
    });
  }

  handleChange(valueAsNumber, valueAsString, input) {
    this.setState({[input.name]: valueAsNumber}, () => {
      if(this.timeout) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.initData();
      }, 1000);
    });
  }

  chart(e, num) {
    e.preventDefault();
    var i = 0;
    var root = ReactDOM.findDOMNode(this);
    var tabs = root.getElementsByClassName('nav-item');
    for(var tab of tabs) {
      i++;
      if(i === num)
        tab.classList.add('active');
      else
        tab.classList.remove('active');
    }
    this.setState({ shownChart: num});
  }

  addModifier(e) {
    var modifiers = this.state.modifiers;
    modifiers.push({
      id: ID(),
      isValid: false,
      isActive: true
    });
    this.setState({ modifiers });
  }

  deleteModifier(e,c) {
    var modifiers = this.state.modifiers;
    var doInit = true;
    modifiers.map((modifier, index) => {
      if(modifier.id === c.props.id) {
        if(!modifier.isValid)
          doInit = false;
        modifiers.splice(index, 1);
      }
      return null;
    });
    this.setState({
      modifiers
    }, () => {
      if(doInit)
        this.initData()
    });
  }

  modifierChange(modifier) {
    var modifiers = this.state.modifiers;
    var index = modifiers.findIndex((e) => e.id === modifier.state.id);
    modifiers[index] = modifier.state;
    this.setState({
      modifiers
    }, () => {
      if(modifier.state.isValid)
        this.initData();
    });
  }

  chartClick(e) {
    // TODO: Maybe rethink this chart click scenario.
    // return;
    // var points;
    // if(this.state.shownChart === 1)
    //   points = this.refs.revenueChart.getPointsAtEvent(e);
    // if(this.state.shownChart === 2)
    //   points = this.refs.trafficChart.getPointsAtEvent(e);
    // var modifiers = this.state.modifiers;
    // var index = -1;
    // for(var modifier of modifiers) {
    //   if(modifier.period_start === points[0].label)
    //     return;
    // }
    // if(index > -1)
    //   return;
    // this.idIndex++;
    // modifiers.push({
    //   id: this.idIndex,
    //   period_start: points[0].label,
    //   isValid: false
    // });
    // this.setState({modifiers});
  }

  lerp (start, end, amt) {
    return (1 - amt) * start + amt * end
  }

  toggleModifiers(e) {
    var showModifiers = !this.state.showModifiers;
    this.setState({showModifiers});
  }

  toggleInitial(e) {
    var showInitial = !this.state.showInitial;
    this.setState({showInitial});
  }

  handleFiles = file => {
    var state = JSON.parse(atob(file.base64.split(',')[1]));
    this.setState(state, () => {
      this.initData();
    });
  }

  render() {
    return (
      <div>
        <div className="flex">
          <ReactFileReader fileTypes="application/json" base64={true} handleFiles={this.handleFiles}>
            <button className='btn btn-primary'>Upload JSON</button>
          </ReactFileReader>
          <FileSaver />
        </div>
        <h3>
        Initial Metrics&nbsp;
        <button className="btn btn-xs btn-default mb-10" onClick={(e) => this.toggleInitial(e)}>
          <span className="glyphicon glyphicon-eye-close"></span> {this.state.showInitial ? "Hide" : "Show"}
        </button>
        </h3>
        {this.state.showInitial &&
        <div className="row p-16-sides">
          <div className="bg-panel p-10">
          <div className="row">
            <form className="form-horizontal">
              <div className="col-md-3 col-sm-4 col-xs-12">
                <label className="control-label lb-sm" htmlFor="mrr">MRR ($/month)</label>
                <MyTooltip text="Monthly recurring revenue." />
                <NumericInput className="form-control" name="mrr" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.mrr} onChange={(a,b,c) => this.handleChange(a,b,c)} />
              </div>
              <div className="col-md-3 col-sm-4 col-xs-12">
                <label className="control-label lb-sm" htmlFor="bank">Bank ($)</label>
                <MyTooltip text="Money in the bank in the beginning of projection." />
                <NumericInput className="form-control" name="bank" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.bank} onChange={(a,b,c) => this.handleChange(a,b,c)} />
              </div>
              <div className="col-md-3 col-sm-4 col-xs-12">
                <label className="control-label lb-sm" htmlFor="arpu">ARPU ($/month)</label>
                <MyTooltip text="Average revenue for each user (per month)" />
                <NumericInput className="form-control" name="arpu" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.arpu} onChange={(a,b,c) => this.handleChange(a,b,c)} />
              </div>
              <div className="col-md-3 col-sm-4 col-xs-12">
                <label className="control-label lb-sm" htmlFor="churn">Revenue Churn (%)</label>
                <MyTooltip text="Percentage of revenue churned per month." />
                <NumericInput className="form-control" name="churn" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.churn} onChange={(a,b,c) => this.handleChange(a,b,c)} />
              </div>
                <div className="col-md-3 col-sm-4 col-xs-12">
                  <label className="control-label lb-sm" htmlFor="traffic">Site Traffic (visits)</label>
                  <MyTooltip text="Number of unique sessions on website." />
                  <NumericInput className="form-control" name="traffic" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.traffic} onChange={(a,b,c) => this.handleChange(a,b,c)} />
                </div>
                <div className="col-md-3 col-sm-4 col-xs-12">
                  <label className="control-label lb-sm" htmlFor="period">Forecast Period (months)</label>
                  <MyTooltip text="The period you wish to forecast for." />
                  <NumericInput className="form-control" name="period" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.period} onChange={(a,b,c) => this.handleChange(a,b,c)} />
                </div>
                <div className="col-md-3 col-sm-4 col-xs-12">
                  <label className="control-label lb-sm" htmlFor="trial_conversion">Visitor to Trial (%)</label>
                  <MyTooltip text="Percentage of website visitors you convert to trial accounts. If you don't offer a trial, leave as 0." />
                  <NumericInput className="form-control" name="trial_conversion" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.trial_conversion} onChange={(a,b,c) => this.handleChange(a,b,c)} />
                </div>
                <div className="col-md-3 col-sm-4 col-xs-12">
                  <label className="control-label lb-sm" htmlFor="subscriber_conversion">Trial to Subscriber (%)</label>
                  <MyTooltip text={<span>Percentage of trial accounts you convert to paid accounts.<br/>If you don't have a trial, leave trial conversion as 0 and enter your visitor to subscriber conversion here instead.</span>} />
                  <NumericInput className="form-control" name="subscriber_conversion" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.subscriber_conversion} onChange={(a,b,c) => this.handleChange(a,b,c)} />
                </div>
                <div className="col-md-3 col-sm-4 col-xs-12">
                  <label className="control-label lb-sm" htmlFor="expenses">Expenses ($)</label>
                  <MyTooltip text="Monthly expenses." />
                  <NumericInput className="form-control" name="expenses" pattern="([0-9]+([.][0-9]*)?|[.][0-9]+)" value={this.state.expenses} onChange={(a,b,c) => this.handleChange(a,b,c)} />
                </div>
              </form>
              </div>
            </div>
          </div>
          }
          <div className="row mb-10">
            <div className="col-lg-12">
              {this.state.modifiers.length > 0 &&
                <div>
                  <h3>Events&nbsp;
                    <button className="btn btn-xs btn-default mb-10" onClick={(e) => this.toggleModifiers(e)}>
                      <span className="glyphicon glyphicon-eye-close"></span> {this.state.showModifiers ? "Hide" : "Show"}
                    </button>
                  </h3>
                  {this.state.showModifiers &&
                  <div className="row">
                    {this.state.modifiers.map((modifier, index) => (
                      <div className="col-sm-3 col-xs-12" key={modifier.id}>
                        <ModifierComponent id={modifier.id}
                        onDelete={(e, c) => this.deleteModifier(e, c)} period={this.state.period}
                        period_start={modifier.period_start} onChange={(modifier) => this.modifierChange(modifier)}
                        interpolation={modifier.interpolation} month={modifier.month} annotation={modifier.annotation}
                        value={modifier.value} type={modifier.type} isActive={modifier.isActive} />
                      </div>
                    ))
                    }
                  </div>
                }
                </div>
              }
              {this.state.showModifiers &&
                <button className="btn btn-info mt-4" onClick={e => this.addModifier(e)}>Add event</button>
              }
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                {this.state.shownChart === 1 &&
                  <Line onClick={(e) => this.chartClick(e)} data={this.state.revenueChartData} options={this.chartOptions} ref="revenueChart"/>
                }
                {this.state.shownChart === 2 &&
                  <Line onClick={(e, x) => this.chartClick(e)} data={this.state.trafficChartData} options={this.chartOptions} ref="trafficChart" />
                }
            </div>
          </div>
          <div className="row">
            <div width="100%">{this.state.status}</div>
          </div>
        </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Projector.js